import React, { useEffect, useState } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Row, Col } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useFormik } from 'formik';

import {
  botRagAdd,
  callexAiAgentOptionsGet
} from '../../../store/slices/tenantSlices/configurationSlice';
import FormSelect from '../../../components/form/FormSelect';
import FormFileUpload from '../../../components/form/FormFileUpload';

const AddBotRagModal = ({ isOpen, toggle, refreshList }) => {
  const dispatch = useDispatch();
  const [agentOptions, setAgentOptions] = useState([]);

  const initialValues = {
    bot_id: '',
    file: null
  };

  const validationSchema = Yup.object({
    bot_id: Yup.string().required('Bot is required'),
    file: Yup.mixed().required('Knowledge file is required')
  });

  const fetchAgentOptions = async () => {
    try {
      const response = await dispatch(callexAiAgentOptionsGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setAgentOptions(options);
      } else {
        setAgentOptions([]);
      }
    } catch (error) {
      setAgentOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchAgentOptions();
    }
  }, [isOpen]);

  const onSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const formData = new FormData();
      formData.append('bot_id', values.bot_id);
      if (values.file) {
        formData.append('file', values.file);
      }

      const response = await dispatch(botRagAdd(formData)).unwrap();
      if (response && response.success) {
        toast.success(response.message || 'RAG file uploaded successfully');
        resetForm();
        toggle();
        refreshList();
      } else {
        toast.error(response?.message || 'Failed to upload RAG file');
      }
    } catch (err) {
      console.error('Error uploading RAG file:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setFieldValue,
    isSubmitting
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  return (
    <Modal isOpen={isOpen} size="lg" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">Add RAG</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="bot-rag-form" onSubmit={handleSubmit}>
          <Row>
            <Col lg="12">
              <FormSelect
                name="bot_id"
                label="Bot / Agent"
                placeholder="Select Bot"
                options={agentOptions}
                value={values.bot_id}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="12">
              <FormFileUpload
                name="file"
                label="Knowledge File (TXT, up to 20MB)"
                placeholder="Select file"
                accept=".txt"
                value={values.file}
                setFieldValue={setFieldValue}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
          </Row>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            toggle();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary"
          disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          form="bot-rag-form"
          type="submit"
          className="px-5 bg-primary border-primary"
          disabled={isSubmitting}>
          {isSubmitting ? 'Uploading...' : 'Upload'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddBotRagModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  refreshList: PropTypes.func.isRequired
};

export default AddBotRagModal;
